import React, { useState } from 'react'
import LiveTrackingMap from './LiveTrackingMap'
import { orderZone, timeIn } from '../../utils/restaurantTime'
import { useMutation, useQuery, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { validateFunc } from '../../constraints/constraints'
import {
  updateOrderStatus,
  updateStatus,
  assignRider,
  getConfiguration,
  getAvailableRiders,
  getRestaurantStaff,
  assignOrderToStaff
} from '../../apollo'
import Loader from 'react-loader-spinner'
import {
  Box,
  Typography,
  Alert,
  Input,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  useTheme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'
import { STATUS_CONFIG, orderTotal } from './OrderCard'
import paymentLabel from '../../utils/paymentLabel'
import { ops, FS, RADIUS, EASE, TONES, tabular, labelSx, focusRing, btn } from './opsTokens'
import {
  StatusChip, FulfilmentTag, InitialsAvatar, MicroTag, Callout, InfoGrid, InfoField, EtaTile,
  SectionTitle, ItemRow, ModLine, SumRow, TotalRow, DriverCard, DriverIdentity, CallButton, ZoomablePhoto
} from './OpsUi'
import { canHaveRider, inStoreLabel } from '../../utils/orderFulfilment'

const UPDATE_STATUS = gql`${updateOrderStatus}`
const MUTATE_STATUS = gql`${updateStatus}`
const ASSIGN_RIDER_MUTATION = gql`${assignRider}`
const GET_CONFIGURATION = gql`${getConfiguration}`
const GET_AVAILABLE_RIDERS = gql`${getAvailableRiders}`
const GET_RESTAURANT_STAFF = gql`${getRestaurantStaff}`
const ASSIGN_ORDER_TO_STAFF = gql`${assignOrderToStaff}`

// Handover choice picked at checkout (addresses.dropoffOption).
const DROPOFF_LABELS = {
  LEAVE_AT_DOOR: 'Leave at my door',
  MEET_AT_DOOR: 'Meet me at the door',
  HAND_IT_TO_ME: 'Hand it to me'
}

// Full order lifecycle steps
const ORDER_STEPS = [
  { status: 'PENDING', label: 'Order Placed' },
  { status: 'ACCEPTED', label: 'Accepted' },
  { status: 'PREPARED', label: 'Food Ready' },
  { status: 'ASSIGNED', label: 'Rider Assigned' },
  { status: 'PICKED', label: 'Picked Up' },
  { status: 'DELIVERED', label: 'Delivered' }
]


function Order(props) {
  const theme = useTheme()
  const { order, t, onClose, onStatusChange } = props
  const [reason, reasonSetter] = useState('')
  const [reasonError, reasonErrorSetter] = useState(null)
  const [error, errorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [selectedRider, setSelectedRider] = useState('')
  const [showRiderSelect, setShowRiderSelect] = useState(false)
  const [selectedStaffId, setSelectedStaffId] = useState('')
  const [showStaffAssign, setShowStaffAssign] = useState(false)
  const [showFdCancel, setShowFdCancel] = useState(false)
  const [fdCancelReason, setFdCancelReason] = useState('')
  const [fdCancelComments, setFdCancelComments] = useState('')

  // Only show staff panel for restaurant admins (not super admin)
  const restaurantId = localStorage.getItem('restaurantId')

  const onCompleted = () => {
    successSetter(t('OrderStatusUpdated'))
    reasonSetter('')
    if (onStatusChange) onStatusChange()
    setTimeout(onDismiss, 4000)
  }
  const onError = err => {
    errorSetter(err.message)
    setTimeout(onDismiss, 5000)
  }

  const { data: configData } = useQuery(GET_CONFIGURATION)
  const { data: ridersData } = useQuery(GET_AVAILABLE_RIDERS)
  const { data: staffData } = useQuery(GET_RESTAURANT_STAFF, { variables: { restaurantId: restaurantId || null }, skip: !restaurantId })
  const currency = configData?.configuration?.currencySymbol || '$'

  const [mutate, { loading }] = useMutation(UPDATE_STATUS, { onError, onCompleted })
  const [mutateStatus, { loading: statusLoading }] = useMutation(MUTATE_STATUS, { onError, onCompleted })
  const [mutateAssignStaff, { loading: staffLoading }] = useMutation(ASSIGN_ORDER_TO_STAFF, {
    onCompleted: () => {
      successSetter('Server assignment saved')
      setShowStaffAssign(false)
      if (onStatusChange) onStatusChange()
      setTimeout(onDismiss, 4000)
    },
    onError
  })
  const [mutateAssignRider, { loading: riderLoading }] = useMutation(ASSIGN_RIDER_MUTATION, {
    onCompleted: () => {
      successSetter('Rider assigned successfully')
      setShowRiderSelect(false)
      setSelectedRider('')
      if (onStatusChange) onStatusChange()
      setTimeout(onDismiss, 4000)
    },
    onError
  })

  const validateReason = () => {
    const err = !validateFunc({ reason }, 'reason')
    reasonErrorSetter(err)
    return err
  }

  const onDismiss = () => {
    errorSetter('')
    successSetter('')
  }

  if (!order) return null

  const status = order.orderStatus
  const isTerminal = status === 'DELIVERED' || status === 'CANCELLED'
  const config = STATUS_CONFIG[status] || { label: status, tone: 'neutral' }
  // For pickup orders, show "Picked Up" instead of "Delivered" in all labels
  const statusLabel = (status === 'DELIVERED' && order.isPickedUp) ? 'Picked Up' : config.label

  // Pickup uses a 4-step flow; self-delivery uses a 5-step flow; platform delivery uses 6-step
  const PICKUP_STEPS = [
    { status: 'PENDING',   label: 'Order Placed' },
    { status: 'ACCEPTED',  label: 'Accepted' },
    { status: 'PREPARED',  label: 'Food Ready' },
    { status: 'DELIVERED', label: 'Picked Up' },
  ]
  const SELF_DELIVERY_STEPS = [
    { status: 'PENDING',   label: 'Order Placed' },
    { status: 'ACCEPTED',  label: 'Accepted' },
    { status: 'PREPARED',  label: 'Food Ready' },
    { status: 'PICKED',    label: 'Out for Delivery' },
    { status: 'DELIVERED', label: 'Delivered' },
  ]
  const inStore = inStoreLabel(order)
  const riderOk = canHaveRider(order)
  const activeSteps = order.isPickedUp || inStore ? PICKUP_STEPS : order.isSelfDelivery ? SELF_DELIVERY_STEPS : ORDER_STEPS
  const activeStepOrder = activeSteps.map(s => s.status)
  const currentStepIndex = activeStepOrder.indexOf(status)

  // Subtotal/delivery already have promo + black-card discounts baked in
  // server-side (orderAmount is post-promo, deliveryCharges is post-blackCard).
  // The displayed discount rows are informational only — don't double-subtract.
  /* The service fee the card was charged. Without it this Total disagreed with
     the "Paid:" figure printed directly beneath it — $25.69 against $26.68 on a
     real order — and the gap was always exactly the fee. */
  const serviceFee = parseFloat(order.serviceFee || 0)
  /* placeOrder takes wallet credit out of orderAmount so the card is charged
     the rest. Wallet is how the customer paid, not a price cut: add it back
     for the Subtotal and Total, and show it as its own payment line. */
  const walletUsed = parseFloat(order.walletAmountUsed || 0)
  const subtotal = (order.orderAmount || 0) + walletUsed
  // Shared with the order lists, so a card and its modal always agree.
  const totalAmount = orderTotal(order)

  const isAnyLoading = loading || statusLoading || riderLoading || staffLoading

  const pad = { xs: 2, md: 3 }
  const noteSx = { '& .MuiTypography-root': { fontSize: FS.sm }, py: 1.25 }
  const fdCancelForm = (
    <Box display="flex" flexDirection="column" gap={1.5} p={1.5} sx={{ border: '1px solid rgba(246,61,104,0.3)', borderRadius: RADIUS.tile, bgcolor: 'rgba(246,61,104,0.04)' }}>
      <FormControl fullWidth size="small">
        <InputLabel>Cancellation Reason *</InputLabel>
        <Select value={fdCancelReason} label="Cancellation Reason *" onChange={e => setFdCancelReason(e.target.value)} sx={{ borderRadius: RADIUS.button, bgcolor: ops.surface }}>
          <MenuItem value="CustomerCancelled">Customer Cancelled</MenuItem>
          <MenuItem value="CustomerChangedOrder">Customer Changed Order</MenuItem>
          <MenuItem value="MissedDropoffTime">Missed Dropoff Time</MenuItem>
          <MenuItem value="CustomerOtherReason">Customer Other Reason</MenuItem>
          <MenuItem value="DriverMissedPickup">Driver Missed Pickup</MenuItem>
          <MenuItem value="UnableToFulfill">Unable To Fulfill</MenuItem>
          <MenuItem value="VendorOtherReason">Vendor Other Reason</MenuItem>
          <MenuItem value="SystemFailure">System Failure</MenuItem>
        </Select>
      </FormControl>
      <TextField
        size="small"
        fullWidth
        label="Additional comments (optional)"
        value={fdCancelComments}
        onChange={e => setFdCancelComments(e.target.value)}
        multiline
        rows={2}
        InputProps={{ sx: { borderRadius: RADIUS.button, bgcolor: ops.surface } }}
      />
      <Button
        variant="contained"
        disableElevation
        size="small"
        disabled={!fdCancelReason || isAnyLoading}
        sx={{ ...btn.solid('#C01048', '#A11043'), '&.Mui-disabled': { bgcolor: ops.sunken, color: ops.faint } }}
        onClick={() => mutate({ variables: { id: order._id, status: 'CANCELLED', reason: fdCancelReason, reasonComments: fdCancelComments.trim() || undefined } })}
      >
        Confirm Cancellation
      </Button>
    </Box>
  )

  const feedback = (success || error) && (
    <Box display="flex" flexDirection="column" gap={1}>
      {success && (
        <Alert variant="filled" severity="success" sx={{ borderRadius: RADIUS.button }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="filled" severity="error" sx={{ borderRadius: RADIUS.button }}>
          {error}
        </Alert>
      )}
    </Box>
  )

  return (
    <Box sx={{ bgcolor: ops.surface }}>
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 3,
          bgcolor: ops.surface,
          borderBottom: `1px solid ${ops.border}`,
          px: pad,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1.5
        }}
      >
        <Box minWidth={0}>
          <Typography component="h2" sx={{ fontSize: FS.xl, fontWeight: 600, color: ops.text, lineHeight: 1.3, letterSpacing: '-0.01em', ...tabular }}>
            Order #{order.orderId}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mt={0.75}>
            <StatusChip toneName={config.tone} label={statusLabel} />
            <FulfilmentTag kind={order.isPickedUp || inStore ? 'pickup' : 'delivery'}>
              {inStore || (order.isPickedUp ? 'Pickup' : order.isSelfDelivery ? 'Restaurant Delivery' : 'Delivery')}
            </FulfilmentTag>
            {order.orderSource === 'MANUAL' && <FulfilmentTag kind="delivery">Phone order</FulfilmentTag>}
          </Box>
        </Box>
        {onClose && (
          <IconButton
            onClick={onClose}
            aria-label="Close"
            size="small"
            sx={{ width: 36, height: 36, borderRadius: RADIUS.button, color: ops.muted, flexShrink: 0, '&:hover': { bgcolor: ops.sunken, color: ops.text }, ...focusRing }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ px: pad, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Status Timeline */}
        {status !== 'CANCELLED' && (
          <Box sx={{ p: 2, borderRadius: RADIUS.tile, bgcolor: ops.subtle, border: `1px solid ${ops.border}` }}>
            <Typography component="div" sx={{ ...labelSx, mb: 2 }}>
              ORDER PROGRESS
            </Typography>
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
              {activeSteps.map((step, idx) => {
                const isCompleted = currentStepIndex > idx || (isTerminal && currentStepIndex === idx)
                const isCurrent = currentStepIndex === idx && !isTerminal
                const isLast = idx === activeSteps.length - 1
                const done = TONES.emerald
                return (
                  <Box
                    component="li"
                    key={step.status}
                    aria-current={isCurrent ? 'step' : undefined}
                    sx={{
                      position: 'relative',
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: { xs: 'row', sm: 'column' },
                      alignItems: { xs: 'center', sm: 'center' },
                      gap: { xs: 1.25, sm: 0.75 },
                      pb: { xs: isLast ? 0 : 2, sm: 0 }
                    }}
                  >
                    {/* Line to the next step, filled once this one is done */}
                    {!isLast && (
                      <Box
                        aria-hidden
                        sx={{
                          position: 'absolute',
                          borderRadius: 1,
                          bgcolor: isCompleted ? done.dot : ops.borderStrong,
                          top: { xs: 26, sm: 11 },
                          left: { xs: 11, sm: 'calc(50% + 16px)' },
                          width: { xs: 2, sm: 'calc(100% - 32px)' },
                          height: { xs: 'calc(100% - 28px)', sm: 2 },
                          transition: `background-color 200ms ${EASE}`
                        }}
                      />
                    )}
                    <Box
                      aria-hidden
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isCompleted ? done.dot : ops.surface,
                        border: `2px solid ${isCompleted || isCurrent ? done.dot : ops.borderStrong}`,
                        boxShadow: isCurrent ? `0 0 0 4px ${done.bg}` : 'none',
                        color: '#fff'
                      }}
                    >
                      {isCompleted ? (
                        <CheckRoundedIcon sx={{ fontSize: 15 }} />
                      ) : isCurrent ? (
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: done.dot }} />
                      ) : null}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: FS.xs,
                        color: isCompleted || isCurrent ? ops.text : ops.muted,
                        fontWeight: isCurrent ? 600 : isCompleted ? 500 : 400,
                        textAlign: { xs: 'left', sm: 'center' },
                        lineHeight: 1.3
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}

        {/* Driver banner: own rider OR First Delivery */}
        {(() => {
          const hasOurRider = !!order.rider && !order.firstDeliveryOrderId
          const hasFD = !!order.firstDeliveryOrderId && (order.fdDriverName || order.orderStatus === 'PREPARED')
          if (!hasOurRider && !hasFD) return null
          const isCancelled = order.orderStatus === 'CANCELLED'
          if (isCancelled) return null

          // Status label (matches restaurant app)
          const statusLabel = ({
            ASSIGNED: 'Heading to restaurant',
            EN_ROUTE_PICKUP: 'En route to restaurant',
            ARRIVED_PICKUP: 'Arrived for pickup',
            PICKED: 'On the way to customer',
            EN_ROUTE_DROPOFF: 'En route to customer',
            ARRIVED_DROPOFF: 'At customer location',
            DELIVERED: 'Delivered',
            COMPLETED: 'Delivered',
          })[order.orderStatus] || 'Assigned'
          const driverStatus = (
            <StatusChip
              size="sm"
              toneName={['DELIVERED', 'COMPLETED'].includes(order.orderStatus) ? 'emerald' : 'sky'}
              label={statusLabel}
            />
          )

          if (hasOurRider) {
            const r = order.rider
            const isStaff = r.driverType === 'RESTAURANT'
            const headerLabel = isStaff ? 'YOUR STAFF DRIVER' : 'RUSHMYFOOD DRIVER'
            const isDeliveredOrDone = ['DELIVERED', 'COMPLETED'].includes(order.orderStatus)
            // Coords for the live map
            const restCoords = order.restaurant?.location?.coordinates
            const destCoords = order.deliveryAddress?.location?.coordinates
              || (order.deliveryAddress?.latitude != null ? [order.deliveryAddress.longitude, order.deliveryAddress.latitude] : null)
            const restLatLng = restCoords ? { lat: parseFloat(restCoords[1]), lng: parseFloat(restCoords[0]) } : null
            const destLatLng = destCoords ? { lat: parseFloat(destCoords[1]), lng: parseFloat(destCoords[0]) } : null
            return (
              <DriverCard label={headerLabel} status={driverStatus}>
                {!isDeliveredOrDone && (restLatLng || destLatLng) && r._id && (
                  <Box sx={{ height: 240, width: '100%', borderRadius: '10px', overflow: 'hidden', border: `1px solid ${ops.border}` }}>
                    <LiveTrackingMap
                      orderStatus={order.orderStatus}
                      restCoordinates={restLatLng}
                      destCoordinates={destLatLng}
                      riderId={r._id}
                    />
                  </Box>
                )}
                <DriverIdentity
                  avatar={<InitialsAvatar name={r.name || '?'} src={r.pictureUrl} size={44} />}
                  name={r.name || 'Driver'}
                  sub={r.phone && <><PhoneOutlinedIcon sx={{ fontSize: 15 }} />{r.phone}</>}
                  action={r.phone && !isDeliveredOrDone && <CallButton href={`tel:${r.phone}`} />}
                />
              </DriverCard>
            )
          }

          // FD branch
          return (
            <DriverCard label="RUSHMYFOOD DRIVER" status={driverStatus}>
              <DriverIdentity
                avatar={<InitialsAvatar name={order.fdDriverName || '?'} src={order.fdDriverPhoto} size={44} />}
                name={order.fdDriverName || 'Awaiting driver…'}
                tags={order.fdDriverTransport ? <MicroTag>{order.fdDriverTransport.toUpperCase()}</MicroTag> : null}
                sub={order.fdDriverPhone && <><PhoneOutlinedIcon sx={{ fontSize: 15 }} />{order.fdDriverPhone}</>}
                action={order.fdDriverPhone && <CallButton href={`tel:${order.fdDriverPhone}`} />}
              />
              {(order.fdPickupEta || order.fdDropoffEta) && (
                <Box display="flex" gap={1}>
                  {order.fdPickupEta && <EtaTile label="PICKUP" value={timeIn(order.fdPickupEta, orderZone(order))} />}
                  {order.fdDropoffEta && <EtaTile label="DROPOFF" value={timeIn(order.fdDropoffEta, orderZone(order))} />}
                </Box>
              )}
              {(order.fdTrackerLink || order.fdDspTrackerLink) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {order.fdTrackerLink && (
                    <Button href={order.fdTrackerLink} target="_blank" rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
                      sx={{ ...btn.secondary, flex: 1, minWidth: 120 }}>
                      Tracker 1
                    </Button>
                  )}
                  {order.fdDspTrackerLink && (
                    <Button href={order.fdDspTrackerLink} target="_blank" rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
                      sx={{ ...btn.secondary, flex: 1, minWidth: 120 }}>
                      Tracker 2
                    </Button>
                  )}
                </Box>
              )}
            </DriverCard>
          )
        })()}

        {/* Customer, Restaurant, Server, Delivery Address */}
        <InfoGrid>
          {order.user && (
            <InfoField label="CUSTOMER">
              <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                <InitialsAvatar name={order.user.name} size={28} />
                <Box minWidth={0}>
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.user.name}</Box>
                  {order.user.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: FS.sm, fontWeight: 400, color: ops.muted, ...tabular }}>
                      <PhoneOutlinedIcon sx={{ fontSize: 14 }} />{order.user.phone}
                    </Box>
                  )}
                </Box>
              </Box>
            </InfoField>
          )}
          {order.restaurant?.name && (
            <InfoField label="RESTAURANT" icon={<StorefrontOutlinedIcon />}>
              {order.restaurant.name}
            </InfoField>
          )}
          {order.assignedStaff && (
            <InfoField label="SERVER">
              {order.assignedStaff.firstName} {order.assignedStaff.lastName}
              <Box sx={{ fontSize: FS.sm, fontWeight: 400, color: ops.muted }}>{order.assignedStaff.role}</Box>
            </InfoField>
          )}
          {/* Delivery Address */}
          {order.deliveryAddress?.deliveryAddress && !order.isPickedUp && (
            <InfoField label="DELIVERY ADDRESS" icon={<PlaceOutlinedIcon />} sx={{ gridColumn: '1 / -1' }}>
              {order.deliveryAddress.deliveryAddress}
              {(() => {
                // Structured unit details (apt/suite, building, entry code) captured
                // at checkout — helps the courier find the exact door.
                const parts = [
                  order.deliveryAddress.label,
                  order.deliveryAddress.apartment,
                  order.deliveryAddress.buildingName,
                  order.deliveryAddress.entryCode && `Code ${order.deliveryAddress.entryCode}`,
                  DROPOFF_LABELS[order.deliveryAddress.dropoffOption],
                ].filter(Boolean)
                return parts.length ? (
                  <Box sx={{ fontSize: FS.sm, fontWeight: 400, color: ops.muted, mt: 0.25 }}>
                    {parts.join(' · ')}
                  </Box>
                ) : null
              })()}
            </InfoField>
          )}
        </InfoGrid>

        {/* Delivery Instructions */}
        {order.instructions && (
          <Box p={1.5} sx={{ bgcolor: TONES.amber.bg, borderRadius: RADIUS.tile, border: '1px solid rgba(247,144,9,0.3)' }}>
            <Typography component="div" sx={{ ...labelSx, color: TONES.amber.fg, mb: 0.5 }}>
              DELIVERY INSTRUCTIONS
            </Typography>
            <Typography sx={{ fontSize: FS.md, color: '#93370D' }}>
              {order.instructions}
            </Typography>
          </Box>
        )}

        {/* Items */}
        <Box>
          <SectionTitle>Items</SectionTitle>
          <Box sx={{ borderTop: `1px solid ${ops.border}` }}>
          {(() => {
            // Parse refunded items: Map of lowercase name → refundQty
            let refundedItemMap = new Map()
            try {
              const raw = order.refundedItems
              const parsed = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null
              if (Array.isArray(parsed)) parsed.forEach(i => refundedItemMap.set(i.name?.toLowerCase(), i.refundQty || i.qty || 0))
            } catch (e) {}
            return order.items?.map((item, idx) => {
            const varPrice = item.variation?.discounted > 0 ? item.variation.discounted : (item.variation?.price || 0)
            // Nested item-as-modifier picks stored on an option (e.g. Fruit Teas -> Mango).
            const nestedTotal = (opt) => (opt.addons || []).reduce(
              (s, ng) => s + (parseFloat(ng.additionalCost) || 0) +
                (ng.options || []).reduce((s2, no) => s2 + parseFloat(no.price || 0) * (no.quantity || 1), 0),
              0
            )
            const addonPrice = (item.addons || []).reduce(
              (sum, addon) =>
                // Flat group fee (e.g. "make any sandwich a plate") charged once per group.
                sum + (parseFloat(addon.additionalCost) || 0) +
                (addon.options || []).reduce((s, o) => s + parseFloat(o.price || 0) * (o.quantity || 1) + nestedTotal(o), 0),
              0
            )
            const itemTotal = (varPrice + addonPrice) * item.quantity
            const refundQty = order.supportTicketStatus === 'RESOLVED' ? (refundedItemMap.get(item.title?.toLowerCase()) || 0) : 0
            const isFullyRefunded = refundQty > 0 && refundQty >= item.quantity
            const isPartiallyRefunded = refundQty > 0 && refundQty < item.quantity
            return (
              <ItemRow
                key={idx}
                qty={`${item.quantity}x`}
                last={idx === order.items.length - 1}
                sx={{ opacity: (isFullyRefunded || isPartiallyRefunded) ? 0.85 : 1 }}
                price={
                  <>
                    <Typography sx={{ fontSize: FS.md, fontWeight: 600, color: isFullyRefunded ? TONES.emerald.fg : ops.text, textDecoration: isFullyRefunded ? 'line-through' : 'none', ...tabular }}>
                      {currency}{itemTotal.toFixed(2)}
                    </Typography>
                    {isPartiallyRefunded && (
                      <Typography sx={{ fontSize: FS.xs, color: TONES.emerald.fg, fontWeight: 600, ...tabular }}>
                        −{currency}{(varPrice * refundQty).toFixed(2)}
                      </Typography>
                    )}
                  </>
                }
              >
                <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                  <Typography sx={{ fontSize: FS.md, fontWeight: 500, color: ops.text, textDecoration: isFullyRefunded ? 'line-through' : 'none' }}>
                    {item.title}
                    {item.variation?.title ? ` (${item.variation.title})` : ''}
                  </Typography>
                  {isFullyRefunded && (
                    <StatusChip size="sm" toneName="refunded" label="Refunded" />
                  )}
                  {isPartiallyRefunded && (
                    <StatusChip size="sm" toneName="refunded" label={`${refundQty} refunded`} />
                  )}
                </Box>
                {(item.addons || []).map((addon, ai) => {
                  // Backend sends one entry per option with a `quantity` field.
                  // (Legacy orders may instead send duplicate entries — fall
                  // back to counting those.)
                  const grouped = (addon.options || []).reduce((acc, opt) => {
                    const key = opt._id
                    if (acc[key]) {
                      acc[key].qty += (opt.quantity || 1)
                    } else {
                      acc[key] = { ...opt, qty: opt.quantity || 1 }
                    }
                    return acc
                  }, {})
                  const opts = Object.values(grouped)
                  const groupFee = parseFloat(addon.additionalCost) || 0
                  if (!opts.length && groupFee <= 0) return null
                  return (
                    <React.Fragment key={ai}>
                      <ModLine indent={1} italic>
                        {addon.title}:{groupFee > 0 ? ` (${currency}${groupFee.toFixed(2)})` : ''}
                      </ModLine>
                      {opts.map(opt => (
                        <React.Fragment key={opt._id}>
                          <ModLine indent={2}>
                            {opt.qty > 1 ? `${opt.qty}x ` : ''}+ {opt.title}{opt.price > 0 ? ` (${currency}${(opt.price * opt.qty).toFixed(2)})` : ''}
                          </ModLine>
                          {/* Nested item-as-modifier picks, grouped by the item's
                              modifier group (e.g. Choose Flavor → Mango) */}
                          {(opt.addons || []).map((ng, ni) => (
                            <React.Fragment key={`${opt._id}-${ni}`}>
                              {ng.title ? (
                                <ModLine indent={2.5} italic>
                                  {ng.title}:
                                </ModLine>
                              ) : null}
                              {(ng.options || []).map((no, noi) => (
                                <ModLine key={`${opt._id}-${ni}-${noi}`} indent={3.5}>
                                  • {no.title}{parseFloat(no.price) > 0 ? ` (${currency}${(parseFloat(no.price) * (no.quantity || 1)).toFixed(2)})` : ''}
                                </ModLine>
                              ))}
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  )
                })}
                {item.specialInstructions?.length > 0 && (
                  <ModLine indent={1}>
                    Note: {item.specialInstructions}
                  </ModLine>
                )}
              </ItemRow>
            )
          })
          })()}
          </Box>
          {/* Refunds made outside a support ticket (card refunds from the
              payments pages or the POS) — the row below covers ticket refunds. */}
          {order.supportTicketStatus !== 'RESOLVED' && order.totalRefunded > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} px={1.5} py={1} sx={{ bgcolor: TONES.emerald.bg, borderRadius: RADIUS.button }}>
              <Typography sx={{ fontSize: FS.md, fontWeight: 600, color: TONES.emerald.fg }}>
                {order.paymentStatus === 'REFUNDED' ? 'Fully refunded' : 'Partly refunded'}
              </Typography>
              <Typography sx={{ fontSize: FS.md, fontWeight: 600, color: TONES.emerald.fg, ...tabular }}>−{currency}{parseFloat(order.totalRefunded).toFixed(2)}</Typography>
            </Box>
          )}
          {/* Refund summary row */}
          {order.supportTicketStatus === 'RESOLVED' && order.refundedAmount > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} px={1.5} py={1} sx={{ bgcolor: TONES.emerald.bg, borderRadius: RADIUS.button }}>
              <Typography sx={{ fontSize: FS.md, fontWeight: 600, color: TONES.emerald.fg }}>Refund issued to customer</Typography>
              <Typography sx={{ fontSize: FS.md, fontWeight: 600, color: TONES.emerald.fg, ...tabular }}>−{currency}{parseFloat(order.refundedAmount).toFixed(2)}</Typography>
            </Box>
          )}
        </Box>

        {/* Charges */}
        <Box p={2} sx={{ bgcolor: ops.subtle, borderRadius: RADIUS.tile, border: `1px solid ${ops.border}` }}>
          {/* Discount rows surface BEFORE the line items so it's obvious what was
              taken off. The Subtotal / Delivery Fee values below are already net
              of these discounts (the server applies them at placeOrder time). */}
          {order.promotion?.discountAmount > 0 && (
            <SumRow
              toneName="emerald"
              label={order.promotion.code ? `Promo: ${order.promotion.code}` : 'Promotion Discount'}
              value={`-${currency}${parseFloat(order.promotion.discountAmount).toFixed(2)}`}
            />
          )}
          {order.blackCardDiscount > 0 && (
            <SumRow
              toneName="emerald"
              label="Delivery Fee Discount (Black Card)"
              value={`-${currency}${parseFloat(order.blackCardDiscount).toFixed(2)}`}
            />
          )}
          {[
            { label: 'Subtotal', value: subtotal },
            { label: 'Delivery Fee', value: order.deliveryCharges || 0 },
            { label: 'Tax', value: order.taxationAmount || 0 },
            // Only when there is one: a cash order carries qorFlatFee as split
            // bookkeeping but was never charged a fee, so it must not show a row.
            ...(serviceFee > 0 ? [{ label: 'Service Fee', value: serviceFee }] : []),
          ].map(row => (
            <SumRow key={row.label} label={row.label} value={`${currency}${row.value.toFixed(2)}`} />
          ))}
          {/* Tip breakdown — show split if serverTip/driverTip present, otherwise legacy single tip */}
          {(order.serverTip > 0 || order.driverTip > 0) ? (
            <>
              {order.serverTip > 0 && (
                <SumRow label="Server Tip" value={`${currency}${parseFloat(order.serverTip).toFixed(2)}`} />
              )}
              {order.driverTip > 0 && (
                <SumRow label="Driver Tip" value={`${currency}${parseFloat(order.driverTip).toFixed(2)}`} />
              )}
            </>
          ) : (order.tipping > 0) && (
            <SumRow label="Tip" value={`${currency}${parseFloat(order.tipping).toFixed(2)}`} />
          )}
          <TotalRow label="Total" value={`${currency}${totalAmount.toFixed(2)}`} />
          {walletUsed > 0 && (
            <Box mt={0.75}>
              <SumRow label="Paid from RushmyFood wallet" value={`${currency}${walletUsed.toFixed(2)}`} />
            </Box>
          )}
          <Box display="flex" justifyContent="space-between" gap={2} mt={1}>
            <Typography sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>
              {paymentLabel(order)} — {order.paymentStatus}
            </Typography>
            <Typography sx={{ fontSize: FS.xs, color: ops.muted, whiteSpace: 'nowrap', ...tabular }}>
              Paid: {currency}{(order.paidAmount || 0).toFixed(2)}
            </Typography>
          </Box>
          {order.qorTransactionId && (
            <Box mt={0.5}>
              <Typography sx={{ fontSize: FS.xs, color: ops.muted, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}>
                QorCommerce txn: {order.qorTransactionId}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Delivery photos: pickup receipt, our delivery proof, FD dropoff photo */}
        {(order.pickupReceiptImage || order.deliveryProofImage || order.fdDropoffPhoto) && (
          <Box>
            <SectionTitle>DELIVERY PHOTOS</SectionTitle>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
              {order.pickupReceiptImage && (
                <PhotoThumb label="PICKUP RECEIPT" href={order.pickupReceiptImage} alt="Pickup receipt" />
              )}
              {order.deliveryProofImage && (
                <PhotoThumb label="DELIVERY PROOF" href={order.deliveryProofImage} alt="Delivery proof" />
              )}
              {order.fdDropoffPhoto && order.fdDropoffPhoto !== order.deliveryProofImage && (
                <PhotoThumb label="FD DROPOFF PROOF" href={order.fdDropoffPhoto} alt="FD dropoff proof" />
              )}
            </Box>
          </Box>
        )}

        {/* Staff Assignment Panel — restaurant admin only */}
        {restaurantId && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} sx={{ p: 1.5, border: `1px solid ${ops.border}`, borderRadius: RADIUS.tile }}>
              <Typography component="div" sx={{ ...labelSx, color: ops.text2 }}>
                {order.assignedStaff
                  ? `SERVER: ${order.assignedStaff.firstName} ${order.assignedStaff.lastName}`
                  : 'NO SERVER ASSIGNED'}
              </Typography>
              <Button
                size="small"
                sx={{ ...btn.secondary, minHeight: 30, px: 1.25, fontSize: FS.xs }}
                onClick={() => setShowStaffAssign(s => !s)}
              >
                {order.assignedStaff ? 'Change Server' : 'Assign Server'}
              </Button>
            </Box>
            {showStaffAssign && (
              <Box mt={1} p={1.5} sx={{ bgcolor: ops.subtle, borderRadius: RADIUS.tile, border: `1px solid ${ops.border}` }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Server</InputLabel>
                  <Select
                    value={selectedStaffId}
                    label="Select Server"
                    onChange={e => setSelectedStaffId(e.target.value)}
                    sx={{ borderRadius: RADIUS.button, bgcolor: ops.surface }}
                  >
                    <MenuItem value="">— Unassign —</MenuItem>
                    {(staffData?.restaurantStaff || []).map(s => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.firstName} {s.lastName} ({s.role})
                      </MenuItem>
                    ))}
                    {!(staffData?.restaurantStaff?.length) && (
                      <MenuItem disabled>No active staff members</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  disableElevation
                  fullWidth
                  size="small"
                  sx={{ ...btn.primary, mt: 1 }}
                  disabled={isAnyLoading}
                  onClick={() => mutateAssignStaff({
                    variables: { orderId: order._id, staffId: selectedStaffId || null }
                  })}
                >
                  Save Assignment
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Feedback (no action footer on a finished order, so it shows here) */}
        {isTerminal && feedback}
      </Box>

      {/* Action Buttons — pinned to the bottom of the modal */}
      {!isTerminal && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 3,
            bgcolor: ops.surface,
            borderTop: `1px solid ${ops.border}`,
            boxShadow: '0 -8px 16px -12px rgba(16,24,40,0.18)',
            px: pad,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25
          }}
        >
          {feedback}

          {isAnyLoading && (
            <Box display="flex" justifyContent="center">
              <Loader type="TailSpin" color={theme.palette.primary.main} height={28} width={28} visible />
            </Box>
          )}

          {/* PENDING: Accept or Cancel */}
          {status === 'PENDING' && (
            <Box display="flex" gap={1.5}>
              <Button
                variant="contained"
                disableElevation
                fullWidth
                sx={btn.solid('#067647', '#085D3A')}
                disabled={isAnyLoading}
                onClick={() => mutate({ variables: { id: order._id, status: 'ACCEPTED', reason: '' } })}
              >
                Accept Order
              </Button>
              <Button
                fullWidth
                sx={btn.danger}
                disabled={isAnyLoading}
                onClick={() => {
                  if (validateReason()) {
                    mutate({ variables: { id: order._id, status: 'CANCELLED', reason } })
                  }
                }}
              >
                Cancel
              </Button>
            </Box>
          )}

          {/* ACCEPTED: Mark Ready + optional Assign Rider (not for self-delivery) */}
          {status === 'ACCEPTED' && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" gap={1.5}>
                <Button
                  variant="contained"
                  disableElevation
                  fullWidth
                  sx={btn.solid('#6938EF', '#5925DC')}
                  disabled={isAnyLoading}
                  onClick={() => mutateStatus({ variables: { id: order._id, orderStatus: 'PREPARED' } })}
                >
                  Mark Food Ready
                </Button>
                <Button
                  sx={{ ...btn.danger, px: 2.5 }}
                  disabled={isAnyLoading}
                  onClick={() => {
                    if (validateReason()) {
                      mutate({ variables: { id: order._id, status: 'CANCELLED', reason } })
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
              {riderOk && !order.isSelfDelivery && (
                <Button
                  fullWidth
                  sx={{ ...btn.secondary, color: TONES.sky.fg }}
                  disabled={isAnyLoading}
                  onClick={() => setShowRiderSelect(s => !s)}
                >
                  Assign Rider Early
                </Button>
              )}
            </Box>
          )}

          {/* PREPARED: self-delivery → Mark Out for Delivery; platform delivery → Assign Rider; pickup → info */}
          {status === 'PREPARED' && order.isSelfDelivery && (
            <Button
              variant="contained"
              disableElevation
              fullWidth
              startIcon={<DeliveryDiningOutlinedIcon />}
              sx={btn.solid('#026AA2', '#065986')}
              disabled={isAnyLoading}
              onClick={() => mutateStatus({ variables: { id: order._id, orderStatus: 'PICKED' } })}
            >
              Mark Out for Delivery
            </Button>
          )}
          {status === 'PREPARED' && !order.isPickedUp && !order.isSelfDelivery && order.firstDeliveryOrderId && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Callout center toneName="sky" icon={<LocalShippingOutlinedIcon />} sx={noteSx}>
                Dispatched to Delivery — awaiting driver
              </Callout>
              <Button
                fullWidth
                size="small"
                sx={btn.danger}
                onClick={() => setShowFdCancel(s => !s)}
              >
                Cancel Order
              </Button>
              {showFdCancel && fdCancelForm}
            </Box>
          )}
          {status === 'PREPARED' && riderOk && !order.isSelfDelivery && !order.firstDeliveryOrderId && (
            <Button
              variant="contained"
              disableElevation
              fullWidth
              sx={btn.solid('#026AA2', '#065986')}
              disabled={isAnyLoading}
              onClick={() => setShowRiderSelect(s => !s)}
            >
              Assign Rider
            </Button>
          )}
          {status === 'PREPARED' && (order.isPickedUp || inStore) && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Callout center toneName="violet" icon={<CheckRoundedIcon />} sx={noteSx}>
                Food is ready — waiting for customer pickup
              </Callout>
              <Button
                variant="contained"
                disableElevation
                fullWidth
                sx={btn.solid('#067647', '#085D3A')}
                disabled={isAnyLoading}
                onClick={() => mutateStatus({ variables: { id: order._id, orderStatus: 'DELIVERED' } })}
              >
                Mark as Picked Up
              </Button>
            </Box>
          )}

          {/* ASSIGNED: info + cancel option (platform delivery) */}
          {status === 'ASSIGNED' && !order.isSelfDelivery && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Callout center toneName="sky" icon={<TwoWheelerIcon />} sx={noteSx}>
                Rider has been assigned and will pick up shortly
              </Callout>
              <Button
                fullWidth
                size="small"
                sx={btn.danger}
                onClick={() => setShowFdCancel(s => !s)}
              >
                Cancel Order
              </Button>
              {showFdCancel && fdCancelForm}
            </Box>
          )}

          {/* PICKED: self-delivery → Mark Delivered button; platform delivery → info only */}
          {status === 'PICKED' && order.isSelfDelivery && (
            <Button
              variant="contained"
              disableElevation
              fullWidth
              startIcon={<CheckRoundedIcon />}
              sx={btn.solid('#067647', '#085D3A')}
              disabled={isAnyLoading}
              onClick={() => mutateStatus({ variables: { id: order._id, orderStatus: 'DELIVERED' } })}
            >
              Mark Delivered
            </Button>
          )}
          {status === 'PICKED' && !order.isSelfDelivery && (
            <Callout center toneName="sky" icon={<TwoWheelerIcon />} sx={noteSx}>
              Order is on the way to the customer
            </Callout>
          )}

          {/* Rider Selection Panel */}
          {showRiderSelect && riderOk && (
            <Box p={1.5} sx={{ bgcolor: ops.subtle, borderRadius: RADIUS.tile, border: `1px solid ${ops.border}` }}>
              <Typography component="div" sx={{ ...labelSx, mb: 1 }}>
                SELECT RIDER
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Available Riders</InputLabel>
                <Select
                  value={selectedRider}
                  label="Available Riders"
                  onChange={e => setSelectedRider(e.target.value)}
                  sx={{ borderRadius: RADIUS.button, bgcolor: ops.surface }}
                >
                  {(ridersData?.availableRiders || []).map(rider => (
                    <MenuItem key={rider._id} value={rider._id}>
                      {rider.name}
                      {rider.phone && (
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 1, ...tabular }}>
                          {rider.phone}
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                  {!(ridersData?.availableRiders?.length) && (
                    <MenuItem disabled>No available riders</MenuItem>
                  )}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disableElevation
                fullWidth
                size="small"
                sx={{ ...btn.solid('#026AA2', '#065986'), mt: 1, '&.Mui-disabled': { bgcolor: ops.sunken, color: ops.faint } }}
                disabled={!selectedRider || isAnyLoading}
                onClick={() => mutateAssignRider({ variables: { id: order._id, riderId: selectedRider } })}
              >
                Confirm Assignment
              </Button>
            </Box>
          )}

          {/* Cancellation Reason Input */}
          {(status === 'PENDING' || status === 'ACCEPTED') && (
            <Input
              fullWidth
              placeholder={t('PHReasonIfRejected')}
              value={reason}
              onChange={e => reasonSetter(e.target.value)}
              disableUnderline
              sx={{
                border: `1px solid ${reasonError ? TONES.rose.dot : ops.borderStrong}`,
                borderRadius: RADIUS.button,
                px: 1.5,
                py: 0.5,
                fontSize: FS.md,
                transition: `border-color 150ms ${EASE}`,
                '&.Mui-focused': { borderColor: reasonError ? TONES.rose.dot : ops.text2 }
              }}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

/* A delivery photo: rounded thumbnail that enlarges over the order. */
const PhotoThumb = ({ label, href, alt }) => (
  <Box minWidth={0}>
    <Typography component="div" sx={{ ...labelSx, mb: 0.5 }}>
      {label}
    </Typography>
    <ZoomablePhoto src={href} alt={alt} title={label} />
  </Box>
)

export default withTranslation()(Order)
